import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Target, Sparkles } from 'lucide-react';
import { useGymCommitment } from '@/hooks/useGymCommitment';
import { useGymCheckins } from '@/hooks/useGymCheckins';

export default function CommitmentButton() {
  const { 
    isCommitted, 
    isCompleted, 
    makeCommitment, 
    removeCommitment, 
    loading, 
    isUpdating 
  } = useGymCommitment();
  
  const { isCheckedInToday } = useGymCheckins();
  
  // Don't show if already checked in (commitment is automatically completed)
  const isCheckedIn = isCheckedInToday();
  
  const handleCommitmentClick = async () => {
    if (isCommitted && !isCompleted) {
      // Allow user to remove commitment if they change their mind
      await removeCommitment();
    } else if (!isCommitted) {
      // Make commitment
      await makeCommitment();
    }
    // If completed, do nothing (just celebrate)
  };

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  // State 3: Completed (checked in after making commitment)
  if (isCompleted || (isCommitted && isCheckedIn)) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"
      >
        <div className="flex items-center justify-center gap-2 text-green-700">
          <Sparkles className="h-5 w-5" />
          <span className="font-medium">🎉 Went as planned!</span>
        </div>
        <p className="text-center text-sm text-green-600 mt-1">
          You followed through on your commitment
        </p>
      </motion.div>
    );
  }

  // State 2: Committed (planned to go, but haven't checked in yet)
  if (isCommitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-700">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-medium">✅ Planning to go today</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCommitmentClick}
              disabled={isUpdating}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {isUpdating ? '...' : 'Change mind?'}
            </Button>
          </div>
          <p className="text-xs text-blue-600 mt-1">
            This gentle reminder stays here until you check in
          </p>
        </div>
      </motion.div>
    );
  }

  // State 1: No commitment (default state)
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4"
    >
      <Button 
        onClick={handleCommitmentClick}
        disabled={isUpdating}
        variant="outline"
        className="w-full h-12 text-base font-medium border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200"
      >
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4" />
          {isUpdating ? 'Making commitment...' : '📅 I will go to the gym today'}
        </div>
      </Button>
      <p className="text-center text-xs text-gray-500 mt-2">
        Making a commitment helps bridge intention and action
      </p>
    </motion.div>
  );
}